/**
 * media-download.js — единый helper «сохранить фото/видео в галерею».
 *
 * Telegram WebApp + iOS/Android edge cases решены централизованно.
 * Используется image-viewer.js и openVideoViewer (my-generations.js).
 *
 * Fallback-chain (приоритет):
 *   1. tg.downloadFile (Bot API ≥8.0) — native save-dialog в TG,
 *      файл попадает в Photos (iOS) / Downloads → Gallery (Android).
 *      Ответ «нет» от диалога — выбор юзера, дальше по цепочке НЕ идём.
 *      Нет ответа за TG_DOWNLOAD_ANSWER_TIMEOUT_MS — вызывающий отпускается
 *      с уведомлением, дальше тоже НЕ идём; SDK после этого считается
 *      залипшим, и следующий тап идёт по цепочке ниже.
 *   Фото:
 *   2. navigator.share + File (iOS Safari вне TG) — share-sheet
 *      «Save to Photos»
 *   3. blob + <a download> — desktop browsers, Android Chrome
 *   4. window.open(url) + toast «Long-press to save» — last resort
 *   Видео (никогда не буферизуем в память — 4K это сотни МБ):
 *   2. вне Telegram, same-origin файл → <a download> прямо на URL
 *   3. иначе tg.openLink / window.open — файл сохраняет системный браузер
 *
 * Раньше downloadImage в my-generations.js:
 *   - звал tg.downloadFile без isVersionAtLeast → silent fail на TG <7.4
 *   - без callback → юзер не видел success/error
 *   - hardcoded .jpg расширение → wrong для webp/png/mp4
 *   - не работал в видео-вьюере (там вообще не было download-кнопки)
 *
 * Этот модуль eager-load (`<script>` в index.html), API стабильное.
 */
(function () {
    'use strict';

    // WebApp.downloadFile появился в Bot API 8.0: vendor/telegram-web-app.js
    // бросает WebAppMethodUnsupported при versionAtLeast('8.0') === false.
    // Раньше гейт стоял на 7.4 — клиенты 7.4–7.x проходили его, метод
    // бросал, и мы молча уходили в blob-фолбэк.
    const TG_DOWNLOAD_FILE_MIN_VERSION = '8.0';
    // SDK держит один запрос на скачивание: пока его диалог на экране,
    // повторный вызов бросает эту ошибку (vendor/telegram-web-app.js,
    // WebApp.downloadFile). Это не сбой метода — диалог уже показан юзеру.
    const TG_DOWNLOAD_POPUP_OPENED = 'WebAppDownloadFilePopupOpened';
    // Сколько ждём ответа диалога (событие file_download_requested) —
    // столько же, сколько ждал прежний таймер. Клиент 8.0+ шлёт событие на
    // каждый запрос, но если не прислал, SDK не узнает, что диалог
    // закрыт: его флаг запроса приватный и снимается только этим событием,
    // так что до конца сессии любой downloadFile бросает
    // WebAppDownloadFilePopupOpened. Раньше таймера не было вовсе, и
    // скачивания (фото из image-viewer тоже) молча возвращали false, а
    // кнопка вьюера оставалась заблокированной.
    const TG_DOWNLOAD_ANSWER_TIMEOUT_MS = 30000;
    const TG_DOWNLOAD_NO_ANSWER_MSG = 'Telegram не ответил на скачивание. Нажми «Скачать» ещё раз';

    // Таймер вышел, а ответа нет: считаем, что SDK залип. Тогда его
    // WebAppDownloadFilePopupOpened — не «диалог на экране», а «нативный
    // путь мёртв», и следующий тап идёт по цепочке дальше. Снимается
    // любым ответом SDK: запоздавший ответ значит, что флаг он сбросил.
    let tgDownloadStuck = false;

    const getTg = () => window.APP?.tg || window.Telegram?.WebApp || null;
    // Все уведомления скачивания — $.toast (toast-notifications.js, слой
    // 100000). Save зовут из полноэкранных вьюеров (фото — 50000, видео —
    // 70000), а модалка $.success/$.error (unified-notifications.js) живёт
    // на 10000: «Видео сохранено» пряталось под вьюером и всплывало только
    // после его закрытия. Результат скачивания — короткая весть, а не
    // вопрос с «OK», так что toast уместен и на карточке в ленте. Модалка —
    // запасной путь, если toast не загрузился; у неё нет метода info.
    const notify = (type, msg) => {
        try {
            const ui = window.$;
            if (typeof ui?.toast === 'function') ui.toast(msg, type);
            else if (typeof ui?.[type] === 'function') ui[type](msg);
        } catch (_) { /* notify-shim не загружен */ }
    };

    /** Абсолютный URL: SDK и системный браузер относительных путей не знают. */
    function toAbsoluteUrl(url) {
        try { return new URL(url, location.href).href; } catch (_) { return url; }
    }

    function isSameOrigin(absUrl) {
        try { return new URL(absUrl).origin === location.origin; } catch (_) { return false; }
    }

    /** Клик по временной <a download> — браузер сам качает файл потоком. */
    function clickDownloadLink(href, fileName) {
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Нативный диалог Telegram. Резолвится ответом диалога: true — файл
     * пошёл в загрузку, false — юзер отказался или ответа не дождались.
     *
     * Таймер (TG_DOWNLOAD_ANSWER_TIMEOUT_MS) только отпускает вызывающего:
     * кнопка снова активна, юзер видит, что делать. Качать «сами» по
     * таймеру нельзя: диалог может ещё висеть на экране, а видео в blob —
     * сотни МБ в памяти (так и было до 23.09.2026). Синхронный throw
     * downloadFile (диалог не показан) уходит вызывающему как reject.
     *
     * @param {string} successMsg — уведомление, если «да» пришло после таймера
     */
    function requestTgDownload(tg, absUrl, fileName, successMsg) {
        return new Promise((resolve, reject) => {
            let timedOut = false;
            let answered = false;
            let timer = null;
            try {
                tg.downloadFile({ url: absUrl, file_name: fileName }, (accepted) => {
                    answered = true;
                    clearTimeout(timer);
                    tgDownloadStuck = false;
                    if (!timedOut) {
                        resolve(Boolean(accepted));
                        return;
                    }
                    // Юзер ответил после таймера: вызывающий уже отпущен,
                    // но про начавшуюся загрузку ему надо сказать — иначе
                    // последним словом осталось бы «Telegram не ответил».
                    if (accepted) notify('success', successMsg);
                });
            } catch (e) {
                reject(e);
                return;
            }
            if (answered) return;
            timer = setTimeout(() => {
                timedOut = true;
                tgDownloadStuck = true;
                notify('info', TG_DOWNLOAD_NO_ANSWER_MSG);
                resolve(false);
            }, TG_DOWNLOAD_ANSWER_TIMEOUT_MS);
        });
    }

    /**
     * Видео — только потоком: 4K-файл провайдера весит сотни МБ, fetch→blob
     * на телефоне держал его целиком в памяти (и падал по OOM). Файл отдаём
     * тому, кто умеет качать на диск: браузеру по <a download> (атрибут
     * работает только для same-origin) или системному браузеру по ссылке.
     */
    function saveVideoWithoutBuffering(url, fileName, tg, successMsg) {
        const absUrl = toAbsoluteUrl(url);
        const insideTelegram = Boolean(tg?.initData);
        // WebView Telegram <a download> не обрабатывает — там ссылка
        // уходит системному браузеру (ветка ниже).
        if (!insideTelegram && isSameOrigin(absUrl)) {
            try {
                clickDownloadLink(absUrl, fileName);
                notify('success', successMsg);
                return true;
            } catch (e) {
                console.warn('[media-download] <a download> failed:', e);
            }
        }
        try {
            if (tg?.openLink) tg.openLink(absUrl);
            else window.open(absUrl, '_blank', 'noopener');
            notify('info', 'Видео откроется в браузере — сохрани его оттуда');
        } catch (e) {
            notify('error', 'Не удалось сохранить файл');
        }
        return false;
    }

    /**
     * Угадать расширение по URL или mime-type.
     * Раньше всё сохранялось как .jpg → iOS Photos жаловались на webp.
     */
    function inferExtension(url, mimeHint) {
        if (mimeHint) {
            if (mimeHint.includes('webp')) return 'webp';
            if (mimeHint.includes('png')) return 'png';
            if (mimeHint.includes('mp4') || mimeHint.includes('video')) return 'mp4';
            if (mimeHint.includes('jpeg') || mimeHint.includes('jpg')) return 'jpg';
        }
        // URL-эвристика — fallback.
        try {
            const path = new URL(url, location.origin).pathname.toLowerCase();
            const m = path.match(/\.(jpe?g|png|webp|mp4|mov|webm|gif)(?:$|\?)/);
            if (m) return m[1] === 'jpeg' ? 'jpg' : m[1];
        } catch (_) {}
        return 'jpg';
    }

    /** Чистим имя файла: убираем спецсимволы, ограничиваем длину. */
    function sanitizeFilename(name, ext) {
        const base = (name || 'image')
            // Расширение ставим сами по ext. Вызовы со «video-12.mp4»
            // иначе давали «video-12mp4_<ts>.mp4» — точка вычищалась ниже.
            .replace(/\.(jpe?g|png|webp|mp4|mov|webm|gif)$/i, '')
            .replace(/[^\p{L}\p{N}\s_-]/gu, '')
            .replace(/\s+/g, '_')
            .slice(0, 60) || 'image';
        return `${base}_${Date.now()}.${ext}`;
    }

    /**
     * Сохранить media-файл в галерею устройства.
     *
     * @param {string} url — URL ресурса (наш бэкенд)
     * @param {object} opts
     * @param {string} [opts.fileName] — базовое имя (без расширения)
     * @param {'image'|'video'} [opts.kind='image'] — тип, влияет на UX-сообщения
     * @param {string} [opts.mimeHint] — подсказка для расширения
     * @returns {Promise<boolean>} — true если сохранилось (или загрузка
     *   началась); false — юзер отказался / отдали ссылку браузеру / сбой
     */
    async function saveMediaToDevice(url, opts = {}) {
        if (!url) {
            notify('error', 'Файл недоступен');
            return false;
        }
        const tg = getTg();
        const ext = inferExtension(url, opts.mimeHint);
        const fileName = sanitizeFilename(opts.fileName, ext);
        const kind = opts.kind || 'image';
        const successMsg = kind === 'video' ? 'Видео сохранено' : 'Фото сохранено';

        // 1. Telegram WebApp native (Bot API ≥8.0, см. TG_DOWNLOAD_FILE_MIN_VERSION).
        // Без isVersionAtLeast-guard'а на старых TG-клиентах метод
        // бросает WebAppMethodUnsupported. callback нужен — без него
        // юзер не узнает результат.
        if (tg?.downloadFile && tg.isVersionAtLeast?.(TG_DOWNLOAD_FILE_MIN_VERSION)) {
            try {
                const accepted = await requestTgDownload(tg, toAbsoluteUrl(url), fileName, successMsg);
                if (accepted) {
                    notify('success', successMsg);
                    return true;
                }
                // false → юзер сам закрыл диалог (или ответа не дождались —
                // уведомление уже показано). Скачивать другим способом
                // поверх его отказа нельзя (раньше так и было: тот же файл
                // тянулся ещё раз в blob).
                return false;
            } catch (e) {
                // Диалог уже на экране (повторный тап) — второй не нужен.
                // Но если прошлый запрос остался без ответа, SDK бросает то
                // же самое до конца сессии: нативный путь мёртв, идём дальше.
                if (e?.message === TG_DOWNLOAD_POPUP_OPENED && !tgDownloadStuck) return false;
                // Метод не стартовал (не https, битые параметры) — диалога
                // юзер не видел, идём дальше по цепочке.
                console.warn('[media-download] tg.downloadFile threw:', e);
            }
        }

        if (kind === 'video') {
            return saveVideoWithoutBuffering(url, fileName, tg, successMsg);
        }

        // 2. navigator.share с File — iOS Safari (вне TG) и Android Chrome
        // дают «Save to Photos» / «Save to Gallery» в native share-sheet.
        if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare) {
            try {
                const resp = await fetch(url);
                if (resp.ok) {
                    const blob = await resp.blob();
                    const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({ files: [file] });
                        notify('success', successMsg);
                        return true;
                    }
                }
            } catch (e) {
                // AbortError = юзер закрыл share-sheet, не ошибка.
                if (e?.name !== 'AbortError') {
                    console.warn('[media-download] navigator.share failed:', e);
                }
                if (e?.name === 'AbortError') return false;
            }
        }

        // 3. blob + <a download> — desktop browsers, иногда Android Chrome.
        try {
            const resp = await fetch(url);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            clickDownloadLink(blobUrl, fileName);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            notify('success', successMsg);
            return true;
        } catch (e) {
            console.warn('[media-download] blob fallback failed:', e);
        }

        // 4. Last resort — открыть в новой вкладке, юзер сохранит вручную.
        try {
            window.open(url, '_blank', 'noopener');
            notify('info', 'Нажми и удерживай изображение чтобы сохранить');
            return false;
        } catch (e) {
            notify('error', 'Не удалось сохранить файл');
            return false;
        }
    }

    window.MediaDownload = { save: saveMediaToDevice };
    console.log('[MediaDownload] ✅ Initialized');
})();
